import { DgiWS1Response, DgiWS2Response } from '../services/dgi.service';
import { BusinessCategory, TaxPayer } from '../types/add_commerce.types';

export const sanitizeRut = (rut: string): string => {
  return rut.replace(/\D/g, '');
};

export const validateRutFormat = (rut: string): boolean => {
  return /^\d{12}$/.test(rut);
};

const normalizeForComparison = (str: string): string => {
  return str.toLowerCase().replace(/\s+/g, '');
};

export const mapContributorTypeToTaxPayer = (
  ws2Response: DgiWS2Response,
  taxPayers: TaxPayer[],
): { taxPayer: TaxPayer | null; message: string } => {
  if (!ws2Response.result || !ws2Response.data?.companyInfo?.contributorType) {
    return {
      taxPayer: null,
      message: 'No se pudo obtener tipo de contribuyente desde DGI',
    };
  }

  const contributorType = ws2Response.data.companyInfo.contributorType;
  const normalizedContributorType = normalizeForComparison(contributorType);

  const matchingTaxPayer = taxPayers.find(
    (tp) => normalizeForComparison(tp.description) === normalizedContributorType,
  );

  if (!matchingTaxPayer) {
    return { taxPayer: null, message: 'No se pudo mapear tipo de contribuyente' };
  }

  return {
    taxPayer: matchingTaxPayer,
    message: `Tipo de contribuyente: ${matchingTaxPayer.description}`,
  };
};

export const processDgiResponses = async (
  ws1Response: DgiWS1Response,
  ws2Response: DgiWS2Response,
  businessCategories: BusinessCategory[],
  taxPayers: TaxPayer[],
  setFieldValue?: (name: string, value: string | number) => void,
  fetchBusinessTypes?: (categoryId: number) => Promise<void>,
): Promise<{ successMessages: string[]; hasErrors: boolean }> => {
  let successMessages = [];
  let hasErrors = false;

  if (ws2Response.result && ws2Response.data?.companyInfo?.contributorType) {
    const contributorType = ws2Response.data.companyInfo.contributorType;
    const normalizedContributorType = normalizeForComparison(contributorType);

    const matchingTaxPayer = taxPayers.find(
      (tp) => normalizeForComparison(tp.description) === normalizedContributorType,
    );

    if (matchingTaxPayer && setFieldValue) {
      setFieldValue('taxpayerTypeId', matchingTaxPayer.id);
      successMessages.push(`Tipo de contribuyente: ${matchingTaxPayer.description}`);
    }
  } else {
    console.log('WS2 failed or no contributor type:', {
      result: ws2Response.result,
      contributorType: ws2Response.data?.companyInfo?.contributorType,
    });
    hasErrors = true;
  }

  if (ws1Response.result && ws1Response.data?.exists) {
    let matchingCategory = null;
    const giroCodeMappings: Record<string, string> = {
      '45102': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '45103': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '45109': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '45309': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '45401': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '47631': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '45201': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '45202': 'Servicios y Artículos de Autos - Motos - Bicicletas',
      '52295': 'Servicios y Artículos de Autos - Motos - Bicicletas',

      '47111': 'Hipermercados-Supermercados-Almacenes',
      '47112': 'Hipermercados-Supermercados-Almacenes',
      '47113': 'Hipermercados-Supermercados-Almacenes',
      '47114': 'Hipermercados-Supermercados-Almacenes',
      '47116': 'Hipermercados-Supermercados-Almacenes',
      '47119': 'Hipermercados-Supermercados-Almacenes',
      '47191': 'Hipermercados-Supermercados-Almacenes',
      '47199': 'Hipermercados-Supermercados-Almacenes',
      '47211': 'Hipernercados-Supermercados-Almacenes',
      '47212': 'Hipermercados-Supermercados-Almacenes',
      '47213': 'Hipermercados-Supermercados-Almacenes',
      '47214': 'Hipermercados-Supermercados-Almacenes',
      '47215': 'Hipermercados-Supermercados-Almacenes',
      '47216': 'Hipermercados-Supermercados-Almacenes',
      '47219': 'Hipermercados-Supermercados-Almacenes',
      '47220': 'Hipermercados-Supermercados-Almacenes',
      '47810': 'Hipermercados-Supermercados-Almacenes',
      '47991': 'Hipermercados-Supermercados-Almacenes',
      '10711': 'Hipermercados-Supermercados-Almacenes',
      '46309': 'Hipermercados-Supermercados-Almacenes',
      '46301': 'Hipermercados-Supermercados-Almacenes',
      '10749': 'Hipermercados-Supermercados-Almacenes',
      '56103': 'Hipermercados-Supermercados-Almacenes',

      '47115': 'Free Shop',

      '47300': 'Combustibles',
      '19201': 'Combustibles',
      '19202': 'Combustibles',
      '19209': 'Combustibles',
      '46610': 'Combustibles',

      '47410': 'Servicios y Venta de Insumos informáticos',
      '47735': 'Servicios y Venta de Insumos informáticos',
      '62090': 'Servicios y Venta de Insumos informáticos',
      '95110': 'Servicios y Venta de Insumos informáticos',

      '47510': 'Indumentaria',
      '47639': 'Indumentaria',
      '47711': 'Indumentaria',
      '47712': 'Indumentaria',
      '47713': 'Indumentaria',
      '47714': 'Indumentaria',
      '47715': 'Indumentaria',
      '47716': 'Indumentaria',
      '47717': 'Indumentaria',
      '47718': 'Indumentaria',
      '47719': 'Indumentaria',
      '47749': 'Indumentaria',
      '47820': 'Indumentaria',
      '46412': 'Indumentaria',
      '14101': 'Indumentaria',
      '46413': 'Indumentaria',

      '47521': 'Barracas, Ferreterías y Afines',
      '47523': 'Barracas, Ferreterías y Afines',
      '47524': 'Barracas, Ferreterías y Afines',
      '47525': 'Barracas, Ferreterías y Afines',
      '47731': 'Barracas, Ferreterías y Afines',
      '46632': 'Barracas, Ferreterías y Afines',
      '46639': 'Barracas, Ferreterías y Afines',

      '47530': 'Artículos para el Hogar',
      '47591': 'Artículos para el Hogar',
      '47592': 'Artículos para el Hogar',
      '47593': 'Artículos para el Hogar',
      '47599': 'Artículos para el Hogar',
      '31001': 'Artículos para el Hogar',

      '47611': 'Librerías, Papelerías y Jugueterías',
      '47619': 'Librerías, Papelerías y Jugueterías',
      '47640': 'Librerías, Papelerías y Jugueterías',
      '47736': 'Librerías, Papelerías y Jugueterías',
      '47721': 'Farmacias, ópticas y Perfumerías',
      '47722': 'Farmacias, ópticas y Perfumerías',

      '47729': 'Salud y Bienestar',
      '86202': 'Salud y Bienestar',
      '86201': 'Salud y Bienestar',
      '86909': 'Salud y Bienestar',

      '47737': 'Servicios Profesionales',
      '69201': 'Servicios Profesionales',
      '69101': 'Servicios Profesionales',
      '62010': 'Servicios Profesionales',
      '71101': 'Servicios Profesionales',
      '74909': 'Servicios Profesionales',
      '71103': 'Servicios Profesionales',
      '70209': 'Servicios Profesionales',
      '62020': 'Servicios Profesionales',
      '70201': 'Servicios Profesionales',
      '75000': 'Servicios Profesionales',
      '74902': 'Servicios Profesionales',
      '74901': 'Servicios Profesionales',
      '95120': 'Servicios Profesionales',
      '43210': 'Servicios Profesionales',

      '47420': 'Bienes y Servicios diversos',
      '47529': 'Bienes y Servicios diversos',
      '47594': 'Bienes y Servicios diversos',
      '47595': 'Bienes y Servicios diversos',
      '47723': 'Bienes y Servicios diversos',
      '47732': 'Bienes y Servicios diversos',
      '47733': 'Bienes y Servicios diversos',
      '47734': 'Bienes y Servicios diversos',
      '47739': 'Bienes y Servicios diversos',
      '47890': 'Bienes y Servicios diversos',
      '47910': 'Bienes y Servicios diversos',
      '47999': 'Bienes y Servicios diversos',
      '81301': 'Bienes y Servicios diversos',
      '33140': 'Bienes y Servicios diversos',
      '68101': 'Bienes y Servicios diversos',
      '68201': 'Bienes y Servicios diversos',
      '53200': 'Bienes y Servicios diversos',
      '96099': 'Bienes y Servicios diversos',
      '46900': 'Bienes y Servicios diversos',
      '46699': 'Bienes y Servicios diversos',

      '56101': 'Restaurantes',
      '10750': 'Restaurantes',
      '56102': 'Restaurantes',
      '56301': 'Restaurantes',
      '56109': 'Restaurantes',
      '10712': 'Restaurantes',
      '56290': 'Restaurantes',
      '56104': 'Restaurantes',

      '63110': 'Hoteles/Moteles',
      '55110': 'Hoteles/Moteles',

      '85499': 'Educación y Enseñanza',

      '49210': 'Taxis y limusinas',
      '49221': 'Taxis y limusinas',
      '49229': 'Taxis y limusinas',
      '49231': 'Taxis y limusinas',
      '96021': 'Estética Integral',
      '96029': 'Estética Integral',

      '82300': 'Espectáculos y Actividades Recreativas',
      '56210': 'Espectáculos y Actividades Recreativas',
      '90000': 'Espectáculos y Actividades Recreativas',
    };

    if (ws1Response.data.activities && ws1Response.data.activities.length > 0) {
      for (const activity of ws1Response.data.activities) {
        matchingCategory = businessCategories.find((cat) => cat.name === giroCodeMappings[activity.code]);

        if (matchingCategory) {
          break;
        }
      }
    }

    if (matchingCategory && setFieldValue) {
      setFieldValue('businessCategoryId', matchingCategory.id);
      if (fetchBusinessTypes) {
        try {
          await fetchBusinessTypes(matchingCategory.id);
          successMessages.push(`Rubro: ${matchingCategory.name}`);
        } catch (error) {
          console.error('Error loading business types:', error);
          successMessages.push(`Rubro: ${matchingCategory.name}`);
        }
      } else {
        successMessages.push(`Rubro: ${matchingCategory.name}`);
      }
    }
  } else {
    hasErrors = true;
  }

  return { successMessages, hasErrors };
};
